// atlas/client/src/lib/auth_client.ts — THE LOGIN's client side (spec §4): token math + the /auth API.
export const AUTH_BASE = '/auth';
export const REFRESH_WINDOW_S = 7 * 86400;

function claimsOf(token: string): Record<string, unknown> | null {
  const p = String(token).split('.');
  if (p.length !== 3) return null;
  try { return JSON.parse(atob(p[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>; } catch { return null; }
}
export function tokenExpS(token: string): number | null { const c = claimsOf(token); return c && typeof c.exp === 'number' ? c.exp : null; }
/** A legacy (anonymous, exp-less) token is never "expired" here — the server decides what it admits. */
export function tokenExpired(token: string, nowS = Math.floor(Date.now() / 1000)): boolean { const e = tokenExpS(token); return e !== null && e <= nowS; }
export function tokenNeedsRefresh(token: string, nowS = Math.floor(Date.now() / 1000)): boolean { const e = tokenExpS(token); return e !== null && e > nowS && e - nowS < REFRESH_WINDOW_S; }

export type AuthResult<T> = { ok: true; data: T } | { ok: false; error: string; message: string; retryAfterS?: number };
const MESSAGES: Record<string, string> = {
  bad_credentials: 'Wrong username or password.',
  rate_limited: 'Too many attempts from here — wait a minute.',
  username_taken: 'That username is taken.',
  invalid_token: 'Your session ended — sign in again.',
  network: 'The sign-in service is not reachable. Check your connection and try again.',
  unavailable: 'Sign-in is unavailable right now — try again shortly.',
  refused_transient: 'Could not connect to the game server — try again in a moment.',
  not_https: 'Sign-in needs a secure (https) connection.',
  bad_origin: 'Sign-in is not set up for this address yet — tell the site operator (ATLAS_AUTH_ORIGINS).',
};
export function messageFor(error: string, fallback = 'Something went wrong — try again.'): string { return MESSAGES[error] ?? fallback; }
/** Refusals whose service text stays generic on purpose ("Request refused."): the client's own words say what to do. */
const CLIENT_WORDS = new Set(['bad_origin']);

type Json = Record<string, unknown>;
const UNAVAILABLE = (): { ok: false; error: string; message: string } => ({ ok: false, error: 'unavailable', message: messageFor('unavailable') });
async function post<T>(path: string, body: unknown, valid: (j: Json) => boolean): Promise<AuthResult<T>> {
  let r: Response;
  try { r = await fetch(`${AUTH_BASE}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); }
  catch { return { ok: false, error: 'network', message: messageFor('network') }; }
  let j: Json = {};
  try { j = await r.json() as Json; } catch { /* empty */ }
  if (r.ok) {
    // A 2xx is a success only as JSON of the declared shape: an SPA fallback (200 + the page's HTML) stored the string
    // "undefined" as the session token (final review F7).
    if (!/^application\/json\b/i.test(r.headers.get('content-type') ?? '') || !j || typeof j !== 'object' || !valid(j)) return UNAVAILABLE();
    return { ok: true, data: j as T };
  }
  const error = typeof j.error === 'string' ? j.error : 'unavailable';
  const message = !CLIENT_WORDS.has(error) && typeof j.message === 'string' ? j.message : messageFor(error);
  return { ok: false, error, message, ...(typeof j.retryAfterS === 'number' ? { retryAfterS: j.retryAfterS } : {}) };
}
type Tok = { token: string };
type Codes = { recoveryCodes: string[] };
const isTok = (j: Json): boolean => typeof j.token === 'string' && j.token.length > 0;
const isCodes = (j: Json): boolean => Array.isArray(j.recoveryCodes) && j.recoveryCodes.length >= 1 && j.recoveryCodes.every((c) => typeof c === 'string');
const isStatus = (j: Json): boolean => typeof j.hasAccount === 'boolean' && typeof j.claimed === 'boolean' && typeof j.codesLeft === 'number'
  && (j.exp === null || typeof j.exp === 'number') && (j.username === null || typeof j.username === 'string')
  && (j.session === 'current' || j.session === 'stale' || j.session === 'none') && typeof j.gateRefusesUnclaimed === 'boolean';
export interface AuthStatus {
  hasAccount: boolean; claimed: boolean; codesLeft: number; exp: number | null; username: string | null;
  /** Does this token still pass the connect check? 'none' = an anonymous legacy token. */
  session: 'current' | 'stale' | 'none';
  /** auth_required is on and debug_reducers is off: an unclaimed account is refused at connect. */
  gateRefusesUnclaimed: boolean;
}
const tokAndCodes = (j: Json): boolean => isTok(j) && isCodes(j);
export const authApi = {
  register: (username: string, displayName: string, password: string) => post<Tok & Codes>('register', { username, displayName, password }, tokAndCodes),
  login: (username: string, password: string) => post<Tok>('login', { username, password }, isTok),
  claim: (token: string, password: string) => post<Tok & Codes>('claim', { token, password }, tokAndCodes),
  recover: (username: string, code: string, newPassword: string) => post<Tok>('recover', { username, code, newPassword }, isTok),
  changePassword: (token: string, currentPassword: string, newPassword: string) => post<Tok>('change-password', { token, currentPassword, newPassword }, isTok),
  recoveryCodes: (token: string, password: string) => post<Codes>('recovery-codes', { token, password }, isCodes),
  logoutAll: (token: string) => post<{ ok: true }>('logout-all', { token }, (j) => j.ok === true),
  refresh: (token: string) => post<Tok>('refresh', { token }, isTok),
  status: (token: string) => post<AuthStatus>('status', { token }, isStatus),
};
